/**
 * KAN-744: выбор менеджера с поиском по ФИО. Варианты: "Не назначен" и
 * активные менеджеры. Компонент только выбирает: сохранение делает обёртка
 * (клиент: ClientManagerSelect, заказ: EstimateManagerSelect).
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useAssignableManagers } from '@/hooks/useManagers';
import type { ManagerBrief } from '@/types/manager';

export const NO_MANAGER_LABEL = 'Не назначен';
const NO_EXCLUDED: readonly string[] = [];

interface Props {
  value: ManagerBrief | null | undefined;
  onSelect: (managerId: string | null) => void;
  disabled?: boolean;
  pending?: boolean;
  /** Ошибка сохранения, показывается под селектом. */
  error?: string | null;
  testId?: string;
  /** KAN-750: подпись варианта без менеджера (по умолчанию "Не назначен"). */
  noneLabel?: string;
  /**
   * KAN-810: сообщать о выборе и тогда, когда он совпал с текущим значением
   * (диалог, где выбор "Без менеджера" надо отличить от "ещё не выбрано").
   */
  notifyUnchanged?: boolean;
  /** KAN-869: менеджеры, которых нет в списке (передача: без себя). */
  excludeIds?: readonly string[];
  /** KAN-869: показывать вариант без менеджера (передача: нет). */
  showNone?: boolean;
  /** KAN-965: низкая кнопка (h-8, мелкий текст) для строк списков. */
  compact?: boolean;
}

function normalize(text: string): string {
  return text.toLowerCase().replace(/ё/g, 'е').trim();
}

export function ManagerSelect({
  value,
  onSelect,
  disabled = false,
  pending = false,
  error = null,
  testId = 'manager-select',
  noneLabel = NO_MANAGER_LABEL,
  notifyUnchanged = false,
  excludeIds = NO_EXCLUDED,
  showNone = true,
  compact = false,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  // KAN-828: все активные менеджеры (одно правило useAssignableManagers).
  const { data: managers, isLoading } = useAssignableManagers();

  const options = useMemo(() => {
    const q = normalize(query);
    const shown = excludeIds.length ? managers.filter((m) => !excludeIds.includes(m.id)) : managers;
    return q ? shown.filter((m) => normalize(m.full_name).includes(q)) : shown;
  }, [managers, query, excludeIds]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (managerId: string | null) => {
    setOpen(false);
    setQuery('');
    if (notifyUnchanged || managerId !== (value?.id ?? null)) onSelect(managerId);
  };

  const optionClass = (selected: boolean) =>
    `block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 ${
      selected ? 'font-medium text-gray-900' : 'text-gray-700'
    }`;

  return (
    <div
      ref={rootRef}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`Менеджер: ${value?.full_name ?? noneLabel}`}
        disabled={disabled || pending}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-white ${
          compact ? 'h-8 px-2 text-xs' : 'h-9 px-3 text-sm'
        } disabled:cursor-not-allowed disabled:opacity-60`}
      >
        <span className={`truncate ${value ? 'text-gray-900' : 'text-gray-500'}`}>
          {value?.full_name ?? noneLabel}
        </span>
        {pending ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        )}
      </button>
      {error && (
        <p className="mt-1 text-xs text-red-700" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[14rem] rounded-md border bg-white p-1 shadow-lg">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по ФИО"
            aria-label="Поиск менеджера"
            data-testid={`${testId}-search`}
            className="mb-1 h-8"
          />
          <ul id={listId} role="listbox" aria-label="Менеджеры" className="max-h-60 overflow-y-auto">
            {showNone && (
              <li role="option" aria-selected={!value}>
                <button type="button" className={optionClass(!value)} onClick={() => choose(null)}>
                  {noneLabel}
                </button>
              </li>
            )}
            {options.map((m) => (
              <li key={m.id} role="option" aria-selected={value?.id === m.id}>
                <button
                  type="button"
                  className={optionClass(value?.id === m.id)}
                  onClick={() => choose(m.id)}
                >
                  {m.full_name}
                </button>
              </li>
            ))}
            {isLoading && <li className="px-3 py-1.5 text-xs text-gray-400">Загрузка...</li>}
            {!isLoading && options.length === 0 && (
              <li className="px-3 py-1.5 text-xs text-gray-400">Никого не нашли</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
